import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../axiosConfig';
import {
    Box, Paper, Typography, Tabs, Tab, Card, CardContent,
    Chip, Button, CircularProgress, Alert, Stack,
    IconButton, Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScheduleIcon from '@mui/icons-material/Schedule';

const STATUS_TABS = [
    { key: 'awaiting_patch_test', label: 'Awaiting patch test', color: 'warning' },
    { key: 'confirmed',           label: 'Confirmed',           color: 'success' },
    { key: 'pending_payment',     label: 'Pending payment',     color: 'info'    },
    { key: 'cancelled',           label: 'Cancelled',           color: 'default' },
    { key: 'all',                 label: 'All',                 color: 'default' },
];

const statusLabel = (s) => ({
    pending_payment: 'Pending payment',
    awaiting_patch_test: 'Awaiting patch test',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
}[s] || s);

const statusColor = (s) => ({
    pending_payment: 'info',
    awaiting_patch_test: 'warning',
    confirmed: 'success',
    cancelled: 'error',
}[s] || 'default');

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    });
};

const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const SoloOnlineBookings = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('status') || 'awaiting_patch_test';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [bookings, setBookings] = useState([]);
    const [counts, setCounts] = useState({
        pending_payment: 0, awaiting_patch_test: 0,
        confirmed: 0, cancelled: 0, total: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const statusParam = activeTab === 'all' ? '' : `status=${activeTab}&`;
            const res = await api.get(`/solo/online-bookings?${statusParam}limit=100`);
            setBookings(res.data.bookings || []);
            setCounts(res.data.counts || {});
        } catch (err) {
            console.error('Failed to fetch online bookings:', err);
            setError('Failed to load bookings. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => { fetchBookings(); }, [fetchBookings]);

    const handleTabChange = (_, newTab) => {
        setActiveTab(newTab);
        // Keep URL in sync so the tile deep-link + refresh both work
        if (newTab === 'awaiting_patch_test') {
            setSearchParams({});
        } else {
            setSearchParams({ status: newTab });
        }
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate('/solo-dashboard')} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                        Online Bookings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Bookings made through your public page
                    </Typography>
                </Box>
                <IconButton onClick={fetchBookings} color="primary" title="Refresh">
                    <RefreshIcon />
                </IconButton>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {STATUS_TABS.map(tab => {
                        const count = tab.key === 'all' ? counts.total : (counts[tab.key] || 0);
                        return (
                            <Tab
                                key={tab.key}
                                value={tab.key}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {tab.label}
                                        {count > 0 && (
                                            <Chip
                                                label={count}
                                                size="small"
                                                color={tab.color}
                                                sx={{ height: 20, minWidth: 24 }}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
            </Paper>

            {/* Content */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" action={
                    <Button size="small" onClick={fetchBookings}>Retry</Button>
                }>{error}</Alert>
            ) : bookings.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {activeTab === 'awaiting_patch_test'
                            ? 'No bookings are waiting for a patch test.'
                            : activeTab === 'all'
                                ? 'No online bookings yet. Share your booking page to get started.'
                                : `No ${statusLabel(activeTab).toLowerCase()} bookings.`}
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {bookings.map(b => (
                        <BookingCard
                            key={b.id}
                            booking={b}
                            onAppointmentClick={(id) => navigate(`/solo/bookings?appointment=${id}`)}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
};

const BookingCard = ({ booking: b, onAppointmentClick }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${statusColor(b.status)}.main` }}>
        <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {b.client_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {b.client_phone}{b.client_email ? ` · ${b.client_email}` : ''}
                    </Typography>
                </Box>
                <Chip label={statusLabel(b.status)} color={statusColor(b.status)} size="small" />
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Service
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {b.services && b.services.length > 0
                            ? b.services.map(s => s.name).join(' + ')
                            : (b.service_name || '—')}
                        {b.service_duration > 0 && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({b.service_duration} min)
                            </Typography>
                        )}
                    </Typography>
                    {b.services && b.services.length > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {b.services.length} services
                        </Typography>
                    )}
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Appointment
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(b.appointment_date)} at {b.start_time}
                    </Typography>
                </Box>
                {b.deposit_amount > 0 && (
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Deposit paid
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            £{b.deposit_amount.toFixed(2)}
                        </Typography>
                    </Box>
                )}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Booked
                    </Typography>
                    <Typography variant="body2">
                        {formatDateTime(b.created_at)}
                    </Typography>
                </Box>
            </Box>

            {/* Status-specific notices — honest about what each state means */}
            {b.status === 'awaiting_patch_test' && (
                <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                    Patch test needed before this appointment. The client has been asked to contact you to arrange it.
                </Alert>
            )}
            {b.status === 'cancelled' && b.auto_cancelled_reason && (
                <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                    Auto-cancelled: {b.auto_cancelled_reason}
                </Alert>
            )}
            {b.status === 'pending_payment' && (
                <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                    Waiting for the client to finish paying. Unpaid holds clear automatically after 15 minutes.
                </Alert>
            )}

            {b.appointment_id && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size="small"
                        startIcon={<ScheduleIcon />}
                        onClick={() => onAppointmentClick(b.appointment_id)}
                    >
                        View in diary
                    </Button>
                </Box>
            )}
        </CardContent>
    </Card>
);

export default SoloOnlineBookings;